import { Settings2 } from "lucide-react";
import { ArcadeControls } from "./ArcadeControls";
import { usePreviewAnalysis } from "./usePreviewTempo";
import { musicCueAt } from "@/utils/arcade/musicAnalysis";
import { relativeSteering } from "@/utils/arcade/touchControls";
import { arcadeTelemetry } from "@/utils/arcade/telemetry";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import ShareIcon from "@mui/icons-material/IosShare";
import VibrationIcon from "@mui/icons-material/Vibration";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePlaylist } from "@/contexts/PlaylistContext";
import { bandLevels } from "@/hooks/useAudioAnalyser";
import { useLockPageScroll } from "@/hooks/useLockPageScroll";
import { useFinePointer, usePrefersReducedMotion } from "@/hooks/useMotionPreferences";
import { getOptimizedImageUrl, trackClick } from "@/components/_helpers";
import { createBeatTracker, type AudioFrame } from "@/utils/arcade/beat";
import { ARCADE_GAMES, DIFFICULTY_SCALE, type ArcadeDifficulty, type ArcadeGameKind } from "@/utils/arcade/games";
import { arcadePath } from "@/utils/arcade/lineup";
import { readArcadePrefs, readBestScore, recordBestScore, writeArcadePrefs } from "@/utils/arcade/scores";
import { createRng, type ArcadeTheme } from "@/utils/arcade/theme";
import { createArtKit, createGame, createPostFx, prepareGame, type FrameInput, type GameEngine, type GameStatus } from "./engine";
import { markEngaged } from "@/lib/installPrompt";
import { canVibrate, createHaptics } from "./haptics";
import { shareScore, type ShareOutcome } from "./shareScore";
import { BadgeToast } from "./retention/BadgeToast";
import { GameOverBoard } from "./retention/GameOverBoard";
import { StartCardNote } from "./retention/StartCardNote";
import { UnlockArcadeDialog } from "./retention/UnlockArcadeDialog";
import type { ArcadeRetention } from "./retention/useArcadeRetention";

type Phase = "ready" | "playing" | "paused" | "over";

// Keys the stage owns while a game runs. Captured on window so the site wide
// shortcuts (Space plays and pauses, A opens albums, M opens songs) stay quiet
// mid game.
const GAME_KEYS = new Set([
  "Space", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  "KeyW", "KeyA", "KeyS", "KeyD", "KeyF", "KeyJ", "KeyK", "KeyP", "KeyZ", "KeyX",
  "Digit1", "Digit2", "Digit3", "Digit4", "Escape",
]);

const MAX_PIXEL_RATIO = 2;
const IDLE_FRAME_MS = 80;
const HUD_INTERVAL_MS = 100;

export interface StageSong {
  title: string;
  artwork?: string;
}

interface ArcadeStageProps {
  game: ArcadeGameKind;
  theme: ArcadeTheme;
  songSlug: string;
  song: StageSong;
  difficulty: ArcadeDifficulty;
  onStart?: () => void;
  onSongClick?: () => void;
  // Endless mode: a theme change restyles the running game instead of
  // starting a new one.
  continuous?: boolean;
  heightSx?: object;
  // Free plays, the unlock dialog, leaderboards, streaks and badges.
  retention?: ArcadeRetention;
}

const emptyStatus: GameStatus = { score: 0, lives: 0, maxLives: 0, combo: 0, meter: null, over: false };

export function ArcadeStage({ game, theme, songSlug, song, difficulty, onStart, onSongClick, continuous = false, heightSx, retention }: ArcadeStageProps) {
  const { analyserNode, isPlaying, currentTrack, audioRef: mediaRef, togglePlayPause } = usePlaylist();
  const analysis = usePreviewAnalysis(isPlaying ? currentTrack?.audioUrl : undefined);
  const previewTempo = analysis?.bpm ?? null;
  const analysisRef = useRef(analysis); analysisRef.current = analysis;
  const tempoRef = useRef(previewTempo);
  tempoRef.current = previewTempo;
  const reducedMotion = usePrefersReducedMotion();
  const finePointer = useFinePointer();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const coverRef = useRef<HTMLImageElement | null>(null);
  const art = useMemo(() => createArtKit(() => {
    const image = coverRef.current;
    return image?.complete && image.naturalWidth > 0 ? image : null;
  }), []);
  const post = useMemo(() => createPostFx(), []);
  const sizeRef = useRef({ width: 0, height: 0 });
  const phaseRef = useRef<Phase>("ready");
  const visibleRef = useRef(true);
  const audioRef = useRef({ analyserNode, isPlaying });
  audioRef.current = { analyserNode, isPlaying };
  const inputRef = useRef({
    taps: [] as FrameInput["taps"],
    pointer: null as FrameInput["pointer"],
    pressed: new Set<string>(),
    held: new Set<string>(),
  });
  const runRef = useRef(0);
  const fingersRef = useRef({ primary: null as number | null, down: new Map<number, { x: number; y: number }>() });
  // Per run bookkeeping for the leaderboard and badges, written by the loop
  // without touching React state: seconds actually played, and whether a
  // boss was on when a "win" arrived.
  const playTimeRef = useRef(0);
  const bossOnRef = useRef(false);
  const bossBeatenRef = useRef(false);
  const retentionRef = useRef(retention);
  retentionRef.current = retention;

  const [controlPrefs, setControlPrefs] = useState(readArcadePrefs);
  const controlsRef = useRef({ timingOffsetMs: controlPrefs.timingOffsetMs, largeTargets: controlPrefs.largeTargets });
  Object.assign(controlsRef.current, { timingOffsetMs: controlPrefs.timingOffsetMs, largeTargets: controlPrefs.largeTargets });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [thirdJump, setThirdJump] = useState(false);
  const resumeMusicRef = useRef(false);
  const songsPlayedRef = useRef(0);
  const previousUrlRef = useRef<string>();
  const steerRef = useRef<{ origin: { x: number; y: number }; anchor: { x: number; y: number } } | null>(null);
  const bufferingRef = useRef(false);
  const [buffering, setBuffering] = useState(false);
  const diagnosticRef = useRef<{ game: string; track: string; runId: string; firstDamage: number; frames: number; slow: number; ended: boolean } | null>(null);
  const retryRef = useRef(0);
  const reportEnd = (reason: 'death' | 'exit') => {
    const run = diagnosticRef.current;
    if (!run || run.ended) return;
    run.ended = true;
    arcadeTelemetry(reason, run.game, run.track, { runId: run.runId, durationSec: Math.round(playTimeRef.current * 10) / 10, firstDamageSec: run.firstDamage, slowFrameRate: run.frames ? run.slow / run.frames : 0, mode: continuous ? 'endless' : 'song' });
  };
  const reportEndRef = useRef(reportEnd); reportEndRef.current = reportEnd;
  useEffect(() => {
    const leave = () => reportEndRef.current('exit');
    window.addEventListener('pagehide', leave);
    return () => { leave(); window.removeEventListener('pagehide', leave); };
  }, [game, songSlug]);

  const [phase, setPhaseState] = useState<Phase>("ready");
  const [hud, setHud] = useState<GameStatus>(emptyStatus);
  const [live, setLive] = useState(false);
  const [best, setBest] = useState(() => readBestScore(songSlug, game));
  const [newBest, setNewBest] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vibration, setVibration] = useState(() => readArcadePrefs().vibration);
  const vibrationRef = useRef(vibration);
  vibrationRef.current = vibration;
  const haptics = useMemo(() => createHaptics(), []);
  const [vibrates] = useState(canVibrate);
  const [shareState, setShareState] = useState<ShareOutcome | "working" | null>(null);
  const loadingRef = useRef(true);
  const themeRef = useRef(theme);
  themeRef.current = theme;
  useLockPageScroll(focusMode);

  const info = ARCADE_GAMES[game];

  const setPhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const buildEngine = useCallback(() => {
    const { width, height } = sizeRef.current;
    runRef.current += 1;
    engineRef.current?.dispose?.();
    const theme = themeRef.current;
    engineRef.current = createGame(game, {
      width: Math.max(1, width),
      height: Math.max(1, height),
      theme,
      art,
      reducedMotion,
      difficulty: DIFFICULTY_SCALE[difficulty],
      keyHints: finePointer,
      controls: controlsRef.current,
      rng: createRng(theme.seed + runRef.current * 7919),
    });
    // Dev only: lets browser playtests read the live probe and status.
    if (import.meta.env.DEV) (window as unknown as { __arcade?: unknown }).__arcade = { engine: engineRef.current, game };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, continuous ? null : theme, art, reducedMotion, difficulty, finePointer]);

  useEffect(() => () => engineRef.current?.dispose?.(), []);

  // Load screen: the game's extra code (three.js for Night Drive) and the
  // cover art arrive before the first frame, so nothing pops in half drawn.
  useEffect(() => {
    let active = true;
    // In endless mode a new song only swaps the artwork; the run keeps going.
    const silent = continuous && Boolean(engineRef.current) && !loadingRef.current;
    if (!silent) {
      loadingRef.current = true;
      setLoading(true);
    }
    const cover = new Promise<void>((resolve) => {
      if (!song.artwork) {
        coverRef.current = null;
        resolve();
        return;
      }
      const image = new Image();
      image.decoding = "async";
      image.onload = () => {
        coverRef.current = image;
        resolve();
      };
      image.onerror = () => resolve();
      image.src = getOptimizedImageUrl(song.artwork, { width: 384, height: 384, fit: "cover", quality: 82 });
    });
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 6000));
    Promise.race([Promise.all([prepareGame(game), cover]), timeout]).then(() => {
      if (!active || silent) return;
      loadingRef.current = false;
      setLoading(false);
      if (!(continuous && phaseRef.current === "playing")) buildEngine();
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, song.artwork]);

  useEffect(() => {
    if (continuous) return;
    setBest(readBestScore(songSlug, game));
    setNewBest(false);
    setHud(emptyStatus);
    setPhase("ready");
    if (!loadingRef.current) buildEngine();
  }, [songSlug, game, buildEngine, setPhase, continuous]);

  useEffect(() => {
    if (continuous) engineRef.current?.setTheme?.(theme, song.title);
  }, [continuous, theme, song.title]);

  // Canvas sizing: CSS pixels drive the game, the backing store follows the
  // device pixel ratio up to 2.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const apply = () => {
      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const ratio = Math.min(MAX_PIXEL_RATIO, window.devicePixelRatio || 1);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const hadSize = sizeRef.current.width > 0;
      sizeRef.current = { width, height };
      if (!engineRef.current || !hadSize) buildEngine();
      else engineRef.current.resize(width, height);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(container);
    return () => observer.disconnect();
  }, [buildEngine]);

  const pause = useCallback(() => {
    if (phaseRef.current === "playing") setPhase("paused");
  }, [setPhase]);

  useEffect(() => {
    if (analysis) arcadeTelemetry('analysis', game, song.title, { bpm: Math.round(analysis.bpm * 10) / 10, beats: analysis.beats.length, quietFraction: analysis.quietFraction, source: 'preview' });
  }, [analysis, game, song.title]);

  useEffect(() => {
    const media = mediaRef.current; if (!media) return;
    let waitAt = 0;
    const waiting = () => {
      bufferingRef.current = true; setBuffering(true);
      if (phaseRef.current === 'playing' && !waitAt) waitAt = performance.now();
    };
    const playing = () => {
      bufferingRef.current = false; setBuffering(false);
      if (waitAt) { arcadeTelemetry('transition', game, song.title, { waitMs: Math.round(performance.now() - waitAt) }); waitAt = 0; }
      if (continuous) engineRef.current?.protect?.(2);
    };
    const error = () => { if (phaseRef.current === 'playing') arcadeTelemetry('playback-error', game, song.title, { code: media.error?.code ?? 0 }); };
    media.addEventListener('waiting', waiting); media.addEventListener('playing', playing); media.addEventListener('error', error);
    return () => { media.removeEventListener('waiting', waiting); media.removeEventListener('playing', playing); media.removeEventListener('error', error); };
  }, [mediaRef, continuous, game, song.title]);

  useEffect(() => {
    const url = currentTrack?.audioUrl;
    if (url === previousUrlRef.current) return;
    const previous = previousUrlRef.current; previousUrlRef.current = url;
    if (!continuous || !previous || !url || phaseRef.current !== 'playing') return;
    engineRef.current?.protect?.(2);
    arcadeTelemetry('song-transition', game, song.title);
    songsPlayedRef.current++;
    if (songsPlayedRef.current % 3 === 0) {
      pause(); setUpgradeOpen(true);
      if (audioRef.current.isPlaying) { resumeMusicRef.current = true; togglePlayPause(); }
    }
  }, [currentTrack?.audioUrl, continuous, game, song.title, pause, togglePlayPause, mediaRef]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const container = containerRef.current;
    let observer: IntersectionObserver | null = null;
    if (container && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(([entry]) => {
        visibleRef.current = entry.isIntersecting;
        if (!entry.isIntersecting) pause();
      }, { threshold: 0.2 });
      observer.observe(container);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      observer?.disconnect();
    };
  }, [pause]);

  const finish = useCallback((status: GameStatus) => {
    reportEndRef.current("death");
    setPhase("over");
    if (vibrationRef.current) haptics.play(["crash"], performance.now());
    markEngaged("arcade-run");
    const result = recordBestScore(songSlug, game, status.score);
    setBest(result.best);
    setNewBest(result.isNew);
    trackClick(`arcade:game-over:${game}`, { label: `${info.name} game over`, section: "arcade", track: song.title, value: status.score });
    retentionRef.current?.runEnded({ score: status.score, level: status.level ?? 1, durationSec: Math.round(playTimeRef.current), bossBeaten: bossBeatenRef.current });
  }, [game, haptics, info.name, setPhase, songSlug, song.title]);

  const toggleVibration = () => {
    const next = !vibration;
    setVibration(next);
    writeArcadePrefs({ vibration: next });
    if (next) haptics.play(["score"], performance.now());
    else haptics.stop();
  };

  const share = async () => {
    setShareState("working");
    const url = `${window.location.origin}${arcadePath(songSlug, game)}`;
    const outcome = await shareScore({ canvas: canvasRef.current, theme, gameName: info.name, songTitle: song.title, score: hud.score, level: hud.level, url });
    setShareState(outcome);
    trackClick(`arcade:share:${game}`, { label: `${info.name} share score`, section: "arcade", track: song.title, value: hud.score });
  };

  // The loop. While playing, the player drives the game. On the start screen
  // the autopilot plays a demo behind the overlay. Paused and game over keep
  // drawing at a low rate so the scene still breathes with the music.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const tracker = createBeatTracker();
    let bins: Uint8Array<ArrayBuffer> | null = null;
    let frameId = 0;
    let last = performance.now();
    let lastDraw = 0;
    let lastHud = 0;
    let lastLive = false;

    const frame = (now: number) => {
      frameId = requestAnimationFrame(frame);
      const engine = engineRef.current;
      if (!engine || !visibleRef.current || loadingRef.current) {
        last = now;
        return;
      }
      const phaseNow = phaseRef.current;
      const playing = phaseNow === "playing" && !(continuous && bufferingRef.current && audioRef.current.isPlaying);
      const attract = phaseNow === "ready" && !reducedMotion;
      if (!playing && !attract && now - lastDraw < IDLE_FRAME_MS) return;
      if (playing && diagnosticRef.current) { diagnosticRef.current.frames++; if (now - last > 50) diagnosticRef.current.slow++; }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      lastDraw = now;

      const { analyserNode: analyser, isPlaying: audioPlaying } = audioRef.current;
      let levels = null;
      if (analyser && audioPlaying) {
        if (!bins || bins.length !== analyser.frequencyBinCount) bins = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(bins);
        levels = bandLevels(bins, bins.length);
      }
      const audio: AudioFrame = tracker.update(levels, now, tempoRef.current ?? theme.bpm, levels ? bins : null, tempoRef.current !== null);
      const music = analysisRef.current;
      if (music && audioPlaying && mediaRef.current) {
        const position = mediaRef.current.currentTime;
        audio.musicCue = (ahead) => musicCueAt(music, position + ahead);
      }
      if (audio.live !== lastLive) {
        lastLive = audio.live;
        setLive(audio.live);
      }

      const input = inputRef.current;
      if (playing) {
        engine.update(dt, { taps: input.taps, pointer: input.pointer, pressed: input.pressed, held: input.held }, audio);
      } else if (attract) {
        engine.update(dt, engine.autopilot(audio), audio);
        if (engine.status().over) buildEngine();
      }
      // Always drain, so the demo's hits never buzz the phone once play starts.
      const feedback = engine.feedback();
      if (playing && vibrationRef.current) haptics.play(feedback, now);
      if (playing) {
        playTimeRef.current += dt;
        if (diagnosticRef.current && diagnosticRef.current.firstDamage < 0 && feedback.some(kind => kind === 'hurt' || kind === 'crash')) diagnosticRef.current.firstDamage = Math.round(playTimeRef.current * 10) / 10;
        for (let i = 0; i < feedback.length; i++) {
          if (feedback[i] === "boss") bossOnRef.current = true;
          else if (feedback[i] === "win" && bossOnRef.current) {
            bossOnRef.current = false;
            bossBeatenRef.current = true;
          }
        }
      }
      input.taps = [];
      input.pressed = new Set();
      if (input.pointer) input.pointer = { ...input.pointer, moved: false };

      const ratio = canvas.width / Math.max(1, sizeRef.current.width);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
      engine.draw(context, audio);
      post.apply(context, canvas, canvas.width, canvas.height, audio, engine.bloom * (reducedMotion ? 0.6 : 1));

      if (playing && now - lastHud > HUD_INTERVAL_MS) {
        lastHud = now;
        const status = engine.status();
        setHud((previous) =>
          previous.score === status.score && previous.lives === status.lives && previous.combo === status.combo && previous.meter === status.meter && previous.level === status.level && previous.fever === status.fever
            ? previous
            : status,
        );
        if (status.over) finish(status);
      }
    };
    frameId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(frameId);
  }, [theme.bpm, finish, post, reducedMotion, buildEngine, continuous, haptics, mediaRef]);

  const begin = useCallback(() => {
    if (phaseRef.current === "over" || phaseRef.current === "ready") {
      if (diagnosticRef.current) retryRef.current++;
      diagnosticRef.current = { game, track: song.title, runId: crypto.randomUUID(), firstDamage: -1, frames: 0, slow: 0, ended: false };
      arcadeTelemetry('run', game, song.title, { runId: diagnosticRef.current.runId, retry: retryRef.current > 0, mode: continuous ? 'endless' : 'song' });
      songsPlayedRef.current = 0; previousUrlRef.current = undefined; resumeMusicRef.current = false; setThirdJump(false);
      buildEngine();
      setHud(engineRef.current?.status() ?? emptyStatus);
      setNewBest(false);
      setShareState(null);
      playTimeRef.current = 0;
      bossOnRef.current = false;
      bossBeatenRef.current = false;
      trackClick(`arcade:start:${game}`, { label: `${info.name} start`, section: "arcade", track: song.title });
      retentionRef.current?.runStarted();
      onStart?.();
    }
    if (resumeMusicRef.current) { resumeMusicRef.current = false; togglePlayPause(); }
    inputRef.current = { taps: [], pointer: null, pressed: new Set(), held: new Set() };
    fingersRef.current = { primary: null, down: new Map() };
    setPhase("playing");
    containerRef.current?.focus({ preventScroll: true });
  }, [buildEngine, game, info.name, onStart, setPhase, song.title, continuous, togglePlayPause]);

  // Start and Play again ask the play gate first; Resume never does.
  const start = useCallback(() => {
    const fresh = phaseRef.current === "over" || phaseRef.current === "ready";
    if (fresh && retentionRef.current && !retentionRef.current.requestStart(begin)) return;
    begin();
  }, [begin]);

  // Clerk's sign in opens under the full screen stage, so leave it when the
  // unlock dialog comes up.
  const dialogOpen = Boolean(retention?.dialog.open);
  useEffect(() => {
    if (dialogOpen) setFocusMode(false);
  }, [dialogOpen]);

  useEffect(() => {
    if (phase !== "playing") return;
    const down = (event: KeyboardEvent) => {
      if (!GAME_KEYS.has(event.code) || event.metaKey || event.ctrlKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.code === "Escape" || event.code === "KeyP") {
        pause();
        return;
      }
      if (!event.repeat) inputRef.current.pressed.add(event.code);
      inputRef.current.held.add(event.code);
    };
    const up = (event: KeyboardEvent) => {
      if (!GAME_KEYS.has(event.code)) return;
      inputRef.current.held.delete(event.code);
    };
    const blur = () => inputRef.current.held.clear();
    window.addEventListener("keydown", down, true);
    window.addEventListener("keyup", up, true);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keyup", up, true);
      window.removeEventListener("blur", blur);
    };
  }, [phase, pause]);

  useEffect(() => {
    if (!focusMode) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.code === "Escape" && phaseRef.current !== "playing") setFocusMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusMode]);

  const pointFor = (event: React.PointerEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  // Fingers are tracked by id. The first one down steers (the game's
  // pointer); any other finger only taps, flagged as secondary, so lifting
  // it never lets go of the steering finger.
  const onPointerDown = (event: React.PointerEvent) => {
    if (phaseRef.current !== "playing") return;
    event.preventDefault();
    try {
      (event.target as Element).setPointerCapture?.(event.pointerId);
    } catch {
      // Capture is a nicety; a pointer the browser no longer tracks must not drop the input.
    }
    const point = pointFor(event);
    const fingers = fingersRef.current;
    const secondary = fingers.primary !== null && fingers.primary !== event.pointerId;
    fingers.down.set(event.pointerId, point);
    inputRef.current.taps.push(secondary ? { ...point, secondary: true } : point);
    if (secondary) return;
    fingers.primary = event.pointerId;
    const relative = controlPrefs.touchMode === 'relative' && (game === 'bass-blaster' || game === 'night-drive');
    const anchor = inputRef.current.pointer ?? { x: sizeRef.current.width / 2, y: sizeRef.current.height * 0.86 };
    steerRef.current = relative ? { origin: point, anchor } : null;
    inputRef.current.pointer = { ...(relative ? anchor : point), down: true, moved: true };
  };
  const onPointerMove = (event: React.PointerEvent) => {
    if (phaseRef.current !== "playing") return;
    const point = pointFor(event);
    const fingers = fingersRef.current;
    if (fingers.down.has(event.pointerId)) fingers.down.set(event.pointerId, point);
    if (fingers.primary !== null && event.pointerId !== fingers.primary) return;
    const down = inputRef.current.pointer?.down ?? false;
    if (event.pointerType !== "mouse" && !down) return;
    const mapped = steerRef.current ? relativeSteering(steerRef.current.origin, steerRef.current.anchor, point, controlPrefs.sensitivity, sizeRef.current.width, sizeRef.current.height) : point;
    inputRef.current.pointer = { ...mapped, down, moved: true };
  };
  const onPointerUp = (event: React.PointerEvent) => {
    const fingers = fingersRef.current;
    fingers.down.delete(event.pointerId);
    if (fingers.primary !== null && event.pointerId !== fingers.primary) return;
    // The steering finger lifted: hand steering to a finger still down.
    const next = fingers.down.entries().next();
    if (!next.done) {
      const [id, point] = next.value;
      fingers.primary = id;
      if (steerRef.current) steerRef.current = { origin: point, anchor: inputRef.current.pointer ?? point };
      inputRef.current.pointer = { ...(steerRef.current?.anchor ?? point), down: true, moved: true };
      return;
    }
    fingers.primary = null;
    steerRef.current = null;
    if (inputRef.current.pointer) inputRef.current.pointer = { ...inputRef.current.pointer, down: false };
  };

  const openSong = () => {
    if (phaseRef.current === "playing") setPhase("paused");
    trackClick("arcade:now-playing", { label: "Arcade now playing chip", section: "arcade", track: song.title });
    onSongClick?.();
  };

  // The game over card carries the leaderboard, so on phones it tightens up
  // to fit the stage without scrolling.
  const compactOver = phase === "over" && Boolean(retention);
  const hearts = useMemo(() => Array.from({ length: hud.maxLives }, (_, index) => index < hud.lives), [hud.lives, hud.maxLives]);
  const musicLive = live && Boolean(currentTrack);
  const chipTitle = musicLive && currentTrack ? currentTrack.title || currentTrack.name || song.title : song.title;
  const chipArtwork = musicLive && currentTrack?.artwork ? currentTrack.artwork : song.artwork;
  const hudText = { fontFamily: "Poppins, sans-serif", fontSize: "13px", color: theme.palette.text, lineHeight: 1.2 };
  const controlSx = { color: theme.palette.text, width: controlPrefs.largeTargets ? 52 : 44, height: controlPrefs.largeTargets ? 52 : 44, backgroundColor: "rgba(0,0,0,0.38)", backdropFilter: "blur(8px)", "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" } };

  return (
    <Box
      ref={containerRef}
      tabIndex={-1}
      data-lenis-prevent
      className="ArcadeStage"
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") event.stopPropagation();
      }}
      sx={{
        position: focusMode ? "fixed" : "relative",
        inset: focusMode ? 0 : undefined,
        zIndex: focusMode ? 10015 : 1,
        width: "100%",
        height: focusMode ? "100dvh" : heightSx ?? { xs: "min(76vh, 160vw)", md: "min(68vh, 660px)" },
        minHeight: focusMode ? undefined : "380px",
        borderRadius: focusMode ? 0 : { xs: "18px", md: "22px" },
        overflow: "hidden",
        backgroundColor: theme.palette.backgroundDeep,
        border: focusMode ? "none" : `1px solid color-mix(in srgb, ${theme.palette.primary} 40%, transparent)`,
        boxShadow: focusMode ? "none" : `0 30px 80px -34px ${theme.palette.primary}, 0 0 0 1px rgba(255,255,255,0.03) inset`,
        outline: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <canvas
        ref={canvasRef}
        aria-label={`${info.name} game for ${song.title}`}
        role="img"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(event) => event.preventDefault()}
        style={{ display: "block", touchAction: "none", cursor: phase === "playing" ? "crosshair" : "default" }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
          alignItems: "start",
          gap: "10px",
          p: { xs: "10px 10px 0", md: "14px 16px 0" },
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <ButtonBase
          onClick={openSong}
          aria-label={`${chipTitle}: listening links`}
          sx={{
            justifySelf: "start",
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            maxWidth: "100%",
            minWidth: 0,
            p: "5px 12px 5px 5px",
            borderRadius: "999px",
            textAlign: "left",
            backgroundColor: "rgba(0,0,0,0.42)",
            backdropFilter: "blur(10px)",
            border: `1px solid color-mix(in srgb, ${theme.palette.primary} 35%, transparent)`,
            transition: "border-color 0.2s ease, background-color 0.2s ease",
            "&:hover": { borderColor: theme.palette.primary, backgroundColor: "rgba(0,0,0,0.6)" },
            "&:focus-visible": { outline: `2px solid ${theme.palette.accent}`, outlineOffset: "2px" },
          }}
        >
          <Box sx={{ position: "relative", width: 36, height: 36, flexShrink: 0 }}>
            {chipArtwork ? (
              <Box
                component="img"
                src={getOptimizedImageUrl(chipArtwork, { width: 96, height: 96, fit: "cover", quality: 75 })}
                alt=""
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  objectFit: "cover",
                  display: "block",
                  animation: musicLive && !reducedMotion ? "arcadeChipSpin 6s linear infinite" : "none",
                  "@keyframes arcadeChipSpin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                }}
              />
            ) : null}
            <Box sx={{ position: "absolute", inset: "14px", borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.75)" }} />
          </Box>
          <Box sx={{ minWidth: 0, display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ ...hudText, fontSize: "10px", letterSpacing: "0.16em", textTransform: "uppercase", color: musicLive ? theme.palette.accent : "rgba(255,255,255,0.65)", fontWeight: 700 }}>
              {musicLive ? "Now playing" : "Tap for links"}
            </Typography>
            <Typography sx={{ ...hudText, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: { sm: "150px", md: "220px" } }}>
              {chipTitle}
            </Typography>
          </Box>
        </ButtonBase>

        <Box sx={{ display: "grid", justifyItems: "center", gap: "2px", minWidth: "80px", visibility: phase === "ready" || (phase === "over" && retention) ? "hidden" : "visible" }}>
          <Typography sx={{ ...hudText, fontFamily: "Hanson, Poppins, sans-serif", fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, fontVariantNumeric: "tabular-nums", textShadow: `0 0 18px ${theme.palette.primary}` }} aria-live="off">
            {hud.score.toLocaleString()}
          </Typography>
          <Typography sx={{ ...hudText, fontSize: "11px", opacity: 0.7 }}>Best {Math.max(best, hud.score).toLocaleString()}</Typography>
          <Box sx={{ display: "flex", gap: "6px", alignItems: "center", minHeight: "16px" }}>
            {hud.level && hud.level > 1 ? <Typography sx={{ ...hudText, fontSize: "11px", fontWeight: 700, opacity: 0.85 }}>LV {hud.level}</Typography> : null}
            {hud.combo > 2 ? <Typography sx={{ ...hudText, fontSize: "12px", color: theme.palette.accent, fontWeight: 700 }}>{hud.combo} combo</Typography> : null}
            {hud.fever ? (
              <Box component="span" sx={{ px: "7px", py: "1px", borderRadius: "999px", fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: 800, letterSpacing: "0.1em", color: theme.palette.backgroundDeep, background: `linear-gradient(90deg, ${theme.palette.accent}, ${theme.palette.primary})` }}>FEVER x2</Box>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: "grid", justifyItems: "end", gap: "6px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", pointerEvents: "auto" }}>
            <IconButton aria-label="Timing and touch settings" sx={controlSx} onClick={() => { pause(); if (audioRef.current.isPlaying) { resumeMusicRef.current = true; togglePlayPause(); } setSettingsOpen(true); }}><Settings2 size={20} /></IconButton>
            {phase === "playing" ? (
              <IconButton onClick={pause} aria-label="Pause game" sx={controlSx}>
                <PauseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            ) : null}
            <IconButton onClick={() => setFocusMode((value) => !value)} aria-label={focusMode ? "Exit full screen" : "Play full screen"} aria-pressed={focusMode} sx={controlSx}>
              {focusMode ? <FullscreenExitIcon sx={{ fontSize: 22 }} /> : <FullscreenIcon sx={{ fontSize: 22 }} />}
            </IconButton>
          </Box>
          {hud.maxLives > 0 ? (
            <Box aria-label={`${hud.lives} lives left`} role="status" sx={{ display: "flex", gap: "5px" }}>
              {hearts.map((alive, index) => (
                <Box key={index} sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: alive ? theme.palette.primary : "transparent", border: `2px solid ${theme.palette.primary}`, boxShadow: alive ? `0 0 10px ${theme.palette.primary}` : "none", opacity: alive ? 1 : 0.4 }} />
              ))}
            </Box>
          ) : hud.meter !== null ? (
            <Box role="meter" aria-label="Health" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(hud.meter * 100)} sx={{ width: { xs: "84px", md: "120px" }, height: "8px", borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
              <Box sx={{ width: `${Math.round(hud.meter * 100)}%`, height: "100%", backgroundColor: hud.meter > 0.3 ? theme.palette.primary : theme.palette.danger, boxShadow: `0 0 10px ${theme.palette.primary}`, transition: "width 0.15s linear" }} />
            </Box>
          ) : null}
          <Typography sx={{ ...hudText, fontSize: "11px", opacity: 0.75, display: { xs: "none", sm: "flex" }, alignItems: "center", gap: "6px" }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: musicLive ? theme.palette.accent : "rgba(255,255,255,0.4)" }} />
            {musicLive ? "Reacting to the music" : `Practice beat, ${theme.bpm} BPM`}
          </Typography>
        </Box>
      </Box>

      {buffering && phase === 'playing' && continuous ? <Box role="status" sx={{ position: 'absolute', bottom: 24, left: 16, right: 16, p: 2, color: '#fff', background: '#111b', borderRadius: 2, fontSize: '14px', textAlign: 'center' }}>Loading the next beat… your run is safe.</Box> : null}
      <ArcadeControls open={settingsOpen} close={() => setSettingsOpen(false)} prefs={controlPrefs} change={(next) => { setControlPrefs(writeArcadePrefs(next)); }} />
      {upgradeOpen ? <Box role="dialog" aria-label="Choose your endless upgrade" sx={{ position: 'absolute', inset: 0, zIndex: 5, p: '22px', background: '#101722f5', color: '#f5f7ff', display: 'grid', alignContent: 'center', gap: '14px', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: '23px', fontWeight: 800 }}>Make the next songs yours</Typography>
        <Typography sx={{ fontSize: '15px' }}>Choose an upgrade. Your run and music are paused.</Typography>
        {([{ id: 'shield', title: 'Shield', text: 'Absorb the next hit.' }, { id: 'heart', title: 'Extra life', text: 'Recover one lost life.' }, { id: 'jump', title: 'Third jump', text: 'One more jump in the air for the rest of this run.' }] as const).map(choice => <ButtonBase key={choice.id} disabled={(choice.id === 'jump' && thirdJump) || (choice.id === 'heart' && hud.lives >= hud.maxLives)} onClick={() => { engineRef.current?.upgrade?.(choice.id); if (choice.id === 'jump') setThirdJump(true); arcadeTelemetry('upgrade', game, song.title, { choice: choice.id }); setUpgradeOpen(false); begin(); }} sx={{ p: '16px', minHeight: 64, fontSize: '15px', color: 'inherit', textAlign: 'left', display: 'block', border: '1px solid #7992aa', borderRadius: '12px', '&.Mui-disabled': { opacity: 0.4 } }}><strong>{choice.title}</strong><br />{choice.text}</ButtonBase>)}
      </Box> : null}
      {loading ? (
        <Box
          role="status"
          aria-label={`Loading ${info.name}`}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            display: "grid",
            placeItems: "center",
            background: `radial-gradient(circle at 50% 45%, ${theme.palette.background}, ${theme.palette.backgroundDeep} 70%)`,
          }}
        >
          <Box sx={{ display: "grid", justifyItems: "center", gap: "18px" }}>
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                borderRadius: "50%",
                background: "radial-gradient(circle, #1a1a20 0 30%, #07070a 31% 100%)",
                boxShadow: `0 0 60px -6px ${theme.palette.primary}`,
                animation: reducedMotion ? "none" : "arcadeLoadSpin 1.6s linear infinite",
                "@keyframes arcadeLoadSpin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                "&::after": { content: '""', position: "absolute", inset: "4px", borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", boxShadow: "inset 0 0 0 10px rgba(255,255,255,0.02), inset 0 0 0 20px rgba(255,255,255,0.02)" },
              }}
            >
              {song.artwork ? (
                <Box component="img" src={getOptimizedImageUrl(song.artwork, { width: 120, height: 120, fit: "cover", quality: 75 })} alt="" sx={{ position: "absolute", inset: "32px", width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }} />
              ) : null}
            </Box>
            <Typography sx={{ ...hudText, fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase", color: theme.palette.accent, fontWeight: 700 }}>
              {info.genre}
            </Typography>
            <Typography sx={{ fontFamily: "Hanson", fontWeight: 700, color: theme.palette.text, fontSize: { xs: "26px", md: "30px" }, m: 0, textShadow: `0 0 24px ${theme.palette.primary}` }}>{info.name}</Typography>
            <Box sx={{ width: 180, height: 4, borderRadius: 4, overflow: "hidden", backgroundColor: "rgba(255,255,255,0.12)" }}>
              <Box sx={{ width: "40%", height: "100%", borderRadius: 4, background: `linear-gradient(90deg, ${theme.palette.primary}, ${theme.palette.accent})`, animation: reducedMotion ? "none" : "arcadeLoadBar 1.1s ease-in-out infinite", "@keyframes arcadeLoadBar": { "0%": { transform: "translateX(-100%)" }, "100%": { transform: "translateX(250%)" } } }} />
            </Box>
            <Typography sx={{ ...hudText, opacity: 0.7 }}>{game === "night-drive" ? "Building the city" : "Tuning up"}</Typography>
          </Box>
        </Box>
      ) : null}

      {phase !== "playing" && !loading ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "grid",
            placeItems: "center",
            p: "20px",
            background: phase === "ready"
              ? `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${theme.palette.backgroundDeep} 35%, transparent), color-mix(in srgb, ${theme.palette.backgroundDeep} 60%, transparent))`
              : `radial-gradient(circle at 50% 45%, color-mix(in srgb, ${theme.palette.backgroundDeep} 55%, transparent), color-mix(in srgb, ${theme.palette.backgroundDeep} 88%, transparent))`,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", justifyItems: "center", gap: { xs: compactOver ? "8px" : "10px", md: "12px" }, textAlign: "center", width: "min(440px, 100%)", maxHeight: "100%", overflowY: "auto", p: { xs: compactOver ? "14px 14px" : "18px 18px", md: "22px 28px" }, borderRadius: "24px", backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(12px)", border: `1px solid color-mix(in srgb, ${theme.palette.primary} 30%, transparent)` }}>
            <Typography component="p" sx={{ ...hudText, fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: theme.palette.accent, fontWeight: 700 }}>
              {phase === "over" ? "Game over" : phase === "paused" ? "Paused" : `${info.genre} · ${song.title}`}
            </Typography>
            <Typography component="p" sx={{ fontFamily: "Hanson", fontWeight: 700, color: theme.palette.text, fontSize: { xs: compactOver ? "26px" : "30px", md: "36px" }, lineHeight: 1.05, m: 0, textShadow: `0 0 18px color-mix(in srgb, ${theme.palette.primary} 60%, transparent)` }}>
              {phase === "over" ? hud.score.toLocaleString() : info.name}
            </Typography>

            {phase === "over" ? (
              <>
                <Typography sx={{ ...hudText, fontSize: { xs: "14px", md: "15px" }, opacity: 0.88 }}>
                  {newBest ? "New best for this song." : `Best for this song: ${best.toLocaleString()}`}
                  {hud.level && hud.level > 1 ? ` Reached level ${hud.level}.` : ""}
                </Typography>
                {retention ? <GameOverBoard retention={retention} theme={theme} score={hud.score} /> : null}
              </>
            ) : (
              <>
                {phase === "ready" ? <Typography sx={{ ...hudText, fontSize: "15px", lineHeight: 1.45, opacity: 0.92 }}>{info.howTo.goal}</Typography> : null}
                <Box
                  component="dl"
                  aria-label="Controls"
                  sx={{ m: 0, width: "100%", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: "14px", rowGap: "6px", textAlign: "left", p: "10px 14px", borderRadius: "14px", backgroundColor: "rgba(255,255,255,0.06)" }}
                >
                  {info.howTo.controls.map((control) => (
                    <Box key={control.action} sx={{ display: "contents" }}>
                      <Typography component="dt" sx={{ ...hudText, fontSize: "13px", fontWeight: 700, color: theme.palette.accent }}>{control.action}</Typography>
                      <Typography component="dd" sx={{ ...hudText, fontSize: "13px", m: 0 }}>{finePointer ? control.keys : control.touch}</Typography>
                    </Box>
                  ))}
                </Box>
                {phase === "ready" ? (
                  <Box component="ul" sx={{ m: 0, pl: "18px", textAlign: "left", display: "grid", gap: "3px", width: "100%" }}>
                    {info.howTo.tips.map((tip) => (
                      <Typography component="li" key={tip} sx={{ ...hudText, fontSize: "13px", opacity: 0.85 }}>{tip}</Typography>
                    ))}
                  </Box>
                ) : null}
              </>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: { xs: compactOver ? "8px" : "10px", md: "10px" }, mt: { xs: compactOver ? "2px" : "6px", md: "6px" } }}>
              <ButtonBase
                onClick={start}
                autoFocus={phase === "paused"}
                sx={{
                  minHeight: { xs: compactOver ? "46px" : "52px", md: "52px" },
                  px: { xs: compactOver ? "18px" : "30px", md: "30px" },
                  borderRadius: "999px",
                  gap: "8px",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: compactOver ? "15px" : "16px", md: "16px" },
                  letterSpacing: "0.04em",
                  color: theme.palette.backgroundDeep,
                  background: `linear-gradient(135deg, ${theme.palette.accent}, ${theme.palette.primary})`,
                  boxShadow: `0 10px 26px -12px ${theme.palette.primary}`,
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "translateY(-1px)" },
                  "&:focus-visible": { outline: `2px solid ${theme.palette.text}`, outlineOffset: "3px" },
                }}
              >
                {phase === "over" ? <ReplayIcon sx={{ fontSize: 22 }} /> : <PlayArrowIcon sx={{ fontSize: 22 }} />}
                {phase === "over" ? "Play again" : phase === "paused" ? "Resume" : "Start"}
              </ButtonBase>
              {phase === "over" && hud.score > 0 ? (
                <ButtonBase
                  onClick={() => void share()}
                  disabled={shareState === "working"}
                  sx={{
                    minHeight: { xs: compactOver ? "46px" : "52px", md: "52px" },
                    px: { xs: compactOver ? "18px" : "24px", md: "24px" },
                    borderRadius: "999px",
                    gap: "8px",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: compactOver ? "15px" : "16px", md: "16px" },
                    color: theme.palette.text,
                    border: `1px solid color-mix(in srgb, ${theme.palette.text} 35%, transparent)`,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                    "&:focus-visible": { outline: `2px solid ${theme.palette.text}`, outlineOffset: "3px" },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 20 }} />
                  <Box component="span">Share<Box component="span" sx={{ display: { xs: compactOver ? "none" : "inline", md: "inline" } }}> score</Box></Box>
                </ButtonBase>
              ) : null}
            </Box>
            {retention && phase === "ready" ? <StartCardNote retention={retention} theme={theme} /> : null}
            {phase === "over" && (shareState === "copied" || shareState === "failed") ? (
              <Typography role="status" sx={{ ...hudText, fontSize: "13px", opacity: 0.85 }}>
                {shareState === "copied" ? "Link and score copied. Paste it anywhere." : "Sharing is not available here."}
              </Typography>
            ) : null}
            {/* Desktop browsers expose vibrate() with no motor behind it. */}
            {vibrates && !finePointer && phase !== "over" ? (
              <ButtonBase
                onClick={toggleVibration}
                aria-pressed={vibration}
                sx={{ gap: "6px", minHeight: "36px", px: "12px", borderRadius: "999px", fontFamily: "Poppins, sans-serif", fontSize: "13px", color: theme.palette.text, opacity: 0.8, "&:hover": { opacity: 1 } }}
              >
                <VibrationIcon sx={{ fontSize: 18 }} />
                Vibration {vibration ? "on" : "off"}
              </ButtonBase>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {retention && phase !== "playing" && retention.badgeToast.length > 0 ? <BadgeToast badges={retention.badgeToast} theme={theme} onDone={retention.clearBadgeToast} /> : null}
      {retention ? <UnlockArcadeDialog retention={retention} /> : null}
    </Box>
  );
}

export default ArcadeStage;
